import { AssistantPart } from '@services/modules/AssistantStream'
import appStore from '@stores/appStore'
import { isActionable } from '@stores/assistantActions'
import assistantStore from '@stores/assistantStore'
import { Button } from 'antd'
import { observer } from 'mobx-react'

import { renderAssistantDates } from '../assistantDates'
import { assistantZoneLabel } from '../assistantZoneLabel'
import { KW } from '../ui'

import EmailPreview from './EmailPreview'

const STATUS_TEXT: Record<string, string> = {
  executed: 'Done',
  cancelled: 'Cancelled',
  expired: 'Expired — nothing was changed',
  // Why, and whether anything was changed, is in the assistant's follow-up message: the card
  // cannot know.
  failed: 'Not completed',
  // The server started the action and has not recorded its outcome (yet).
  executing: 'Processing — result not confirmed yet',
}

/*
 * A change the assistant proposes. Nothing has happened yet: only these buttons (the user,
 * never the model) make the server run it, once, after re-checking everything.
 */
const ConfirmationPart = ({ part }: { part: AssistantPart }) => {
  const actionable = isActionable(part)
  const inFlight = assistantStore.actionInFlight === part.actionId
  const blocked = assistantStore.isInputLocked
  const dateOptions = {
    dateFormat: appStore.currentUser?.dateFormat,
    defaultTimeZone: appStore.timezone,
    zoneLabel: assistantZoneLabel,
  }
  const statusText =
    part.status === 'proposed' && !actionable
      ? STATUS_TEXT.expired
      : STATUS_TEXT[part.status ?? '']

  return (
    <div
      className="mt-2 w-full rounded-[10px] px-3 py-[10px]"
      style={{ border: `1px solid ${KW.line2}`, background: '#fff' }}
      role="group"
      aria-label={part.label}
    >
      <div className="text-[13.5px] font-bold" style={{ color: KW.ink }}>
        {part.label}
      </div>
      {part.email ? (
        <>
          <EmailPreview email={part.email} />
          {(part.lines ?? []).map((line, index) => (
            <p
              key={index}
              className="mb-2 mt-0 text-xs"
              style={{ color: KW.muted }}
            >
              {renderAssistantDates(line, dateOptions)}
            </p>
          ))}
        </>
      ) : (
        <ul
          className="my-[6px] list-disc pl-5 text-[12.5px]"
          style={{ color: KW.body }}
        >
          {(part.lines ?? []).map((line, index) => (
            <li key={index}>{renderAssistantDates(line, dateOptions)}</li>
          ))}
        </ul>
      )}

      {actionable ? (
        <div className="flex gap-2">
          <Button
            type="primary"
            size="small"
            loading={inFlight}
            disabled={blocked && !inFlight}
            onClick={() => assistantStore.decideAction(part.actionId, true)}
          >
            {part.confirmLabel || 'Confirm'}
          </Button>
          <Button
            size="small"
            disabled={blocked}
            onClick={() => assistantStore.decideAction(part.actionId, false)}
          >
            Cancel
          </Button>
        </div>
      ) : (
        statusText && (
          <div className="text-xs font-bold" style={{ color: KW.muted }}>
            {statusText}
          </div>
        )
      )}
    </div>
  )
}

export default observer(ConfirmationPart)
